import React, { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { MessageDescriptor } from "react-intl";

import { Button } from "@UI/Components/Button/Button";
import { Checkbox } from "@UI/Components/Checkbox/Checkbox";
import { Divider } from "@UI/Components/Divider/Divider";
import { Icon } from "@UI/Components/Icon/Icon";
import { Envelope } from "@UI/Components/Icons/Envelope";
import { UserIcon } from "@UI/Components/Icons/UserIcon";
import {
  Input,
  InputGroup,
  InputLeftElement,
} from "@UI/Components/Input/Input";
import { PhoneInput } from "@UI/Components/PhoneInput/PhoneInput";
import { VStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { getCurrentCountry, getKnowFrom } from "@config";
import { useRegisterUser } from "@modules/Authentication/hooks/useEmailLogin";
import {
  TUserRegistrationError,
  getUserRegistrationError,
} from "@modules/Authentication/services/emailLogin";

import { AuthFormControl } from "../../Components/AuthFormControl/AuthFormControl";
import {
  authCheckboxStyles,
  authFieldStyles,
  authSubmitStyles,
} from "../../Components/AuthFormControl/authStyles";
import { PasswordInput } from "../../Components/PasswordInput/PasswordInput";
import { TaskCreationOutcomeModals } from "../../Components/TaskCreationOutcomeModals/TaskCreationOutcomeModals";
import {
  AUTH_SCREEN_KEYS,
  NAME_PATTERN,
  PASSWORD_MIN_LENGTH,
} from "../../config/constants";
import { useAuthStepCompletion } from "../../hooks/useAuthStepCompletion";
import { useRequireAuthIdentity } from "../../hooks/useRequireAuthIdentity";
import messages from "../../messages";
import { AuthStepLayout } from "../AuthStepLayout/AuthStepLayout";

const FIELD_IDS = {
  email: "taskCreationRegisterEmail",
  firstName: "taskCreationRegisterFirstName",
  lastName: "taskCreationRegisterLastName",
  password: "taskCreationRegisterPassword",
  phone: "taskCreationRegisterPhone",
} as const;

/** Fixed so a repeated failure replaces its toast instead of stacking. */
const REGISTER_FAILED_TOAST_ID = "taskCreation.auth.register.failed";

/**
 * Refusals no field on this form can fix, so they are toasted rather than
 * pinned to an input. The rest land on the field that owns them, below.
 */
const ACCOUNT_ERROR_MESSAGES: Partial<
  Record<TUserRegistrationError, MessageDescriptor>
> = {
  email_taken: messages["taskCreation.auth.error.emailTaken"],
  email_invalid: messages["taskCreation.auth.email.invalid"],
};

interface IRegistrationFormValues {
  firstName: string;
  lastName: string;
  password: string;
  /** E.164 from `PhoneInput`, or empty — the field is optional. */
  phoneNumber: string;
  proClient: boolean;
  consentEmail: boolean;
}

/** The refusals that name a field the visitor can still fix. */
const FIELD_ERRORS: Partial<
  Record<
    TUserRegistrationError,
    { field: keyof IRegistrationFormValues; message: MessageDescriptor }
  >
> = {
  password_invalid: {
    field: "password",
    message: messages["taskCreation.validation.minLength"],
  },
  phone_number_invalid: {
    field: "phoneNumber",
    message: messages["taskCreation.auth.register.phone.invalid"],
  },
};

/**
 * The account for an address with none. On success the flow ends here the same
 * way it does on the login screen: the endpoint signs the new user in, the
 * draft's claim publishes their request, and the success modal takes over.
 */
export const AuthRegisterScreen = () => {
  const { formatMessage, locale } = useI18n();

  // Bare language, not the full locale: `User::LOCALES_CODE_TO_ENUM` is keyed
  // "fr"/"nl"/"en", and "fr-BE" misses it.
  const language = locale.split("-")[0];

  const identity = useRequireAuthIdentity();

  const { mutateAsync: createAccount } = useRegisterUser();
  const {
    showAccountError,
    publishAndFinish,
    isPublishing,
    publishedRequestId,
  } = useAuthStepCompletion(REGISTER_FAILED_TOAST_ID, AUTH_SCREEN_KEYS.REGISTER);

  const hasAccount = useRef(false);

  /**
   * `PhoneInput` validates the number itself and reports it here — the field is
   * optional, so only a number that was actually typed has to be valid.
   */
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const {
    register,
    control,
    handleSubmit,
    setError,
    // Both halves of Continue, so the button cannot go idle in between.
    formState: { errors, isSubmitting },
  } = useForm<IRegistrationFormValues>({
    // A name field's error should show on the blur that typed it, not wait
    // for a first submit attempt.
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      phoneNumber: "",
      proClient: false,
      consentEmail: false,
    },
  });

  const onSubmit = async (values: IRegistrationFormValues) => {
    if (!identity) return;

    if (values.phoneNumber && !isPhoneValid) {
      setError("phoneNumber", {
        message: formatMessage(
          messages["taskCreation.auth.register.phone.invalid"]
        ),
      });

      return;
    }

    // Skipped once it has succeeded: a retry after a failed publish would be
    // refused `email_taken` by the account this one just created, and the
    // draft would never get its second chance at being claimed.
    if (!hasAccount.current) {
      try {
        await createAccount({
          firstname: values.firstName.trim(),
          lastname: values.lastName.trim(),
          email: identity.email,
          password: values.password,
          // Omitted rather than sent empty: the endpoint rejects a `tel` it
          // cannot parse, and "no number" is not a number it can.
          ...(values.phoneNumber ? { phone_number: values.phoneNumber } : {}),
          pro_client: values.proClient,
          consent_email: values.consentEmail,
          country_code: getCurrentCountry(),
          locale: language,
          know_from: getKnowFrom(),
        });

        hasAccount.current = true;
      } catch (error) {
        const code = getUserRegistrationError(error);
        const fieldError = code && FIELD_ERRORS[code];

        if (fieldError) {
          setError(fieldError.field, {
            message: formatMessage(fieldError.message, {
              min: PASSWORD_MIN_LENGTH,
            }),
          });

          return;
        }

        showAccountError(
          (code && ACCOUNT_ERROR_MESSAGES[code]) ??
            messages["taskCreation.auth.error.registerFailed"]
        );

        return;
      }
    }

    await publishAndFinish();
  };

  if (!identity) return null;

  return (
    <AuthStepLayout
      title={
        <FormattedMessage {...messages["taskCreation.auth.register.title"]} />
      }
    >
      <TaskCreationOutcomeModals
        isPublishing={isPublishing}
        publishedRequestId={publishedRequestId}
      />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <VStack alignItems="stretch" spacing="16px">
          <AuthFormControl
            htmlFor={FIELD_IDS.email}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.email.label"]}
              />
            }
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={Envelope}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              {/* The address the email screen resolved. Read-only rather than
                  disabled, so it is still announced and copyable; Back is what
                  changes it, since another address may already have an
                  account. */}
              <Input
                id={FIELD_IDS.email}
                type="email"
                value={identity.email}
                readOnly
                {...authFieldStyles}
                background="gray.100"
                color="gray.600"
              />
            </InputGroup>
          </AuthFormControl>
          <AuthFormControl
            htmlFor={FIELD_IDS.firstName}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.register.firstName.label"]}
              />
            }
            errorMessage={errors.firstName?.message}
            isRequired
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={UserIcon}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              <Input
                id={FIELD_IDS.firstName}
                autoComplete="given-name"
                isDisabled={isSubmitting}
                placeholder={formatMessage(
                  messages["taskCreation.auth.register.firstName.placeholder"]
                )}
                {...authFieldStyles}
                {...register("firstName", {
                  required: formatMessage(
                    messages["taskCreation.auth.register.firstName.required"]
                  ),
                  pattern: {
                    value: NAME_PATTERN,
                    message: formatMessage(
                      messages["taskCreation.auth.register.firstName.invalid"]
                    ),
                  },
                })}
              />
            </InputGroup>
          </AuthFormControl>
          <AuthFormControl
            htmlFor={FIELD_IDS.lastName}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.register.lastName.label"]}
              />
            }
            errorMessage={errors.lastName?.message}
            isRequired
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={UserIcon}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              <Input
                id={FIELD_IDS.lastName}
                autoComplete="family-name"
                isDisabled={isSubmitting}
                placeholder={formatMessage(
                  messages["taskCreation.auth.register.lastName.placeholder"]
                )}
                {...authFieldStyles}
                {...register("lastName", {
                  required: formatMessage(
                    messages["taskCreation.auth.register.lastName.required"]
                  ),
                  pattern: {
                    value: NAME_PATTERN,
                    message: formatMessage(
                      messages["taskCreation.auth.register.lastName.invalid"]
                    ),
                  },
                })}
              />
            </InputGroup>
          </AuthFormControl>
          <AuthFormControl
            htmlFor={FIELD_IDS.password}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.password.label"]}
              />
            }
            errorMessage={errors.password?.message}
            isRequired
          >
            <PasswordInput
              id={FIELD_IDS.password}
              autoComplete="new-password"
              isDisabled={isSubmitting}
              placeholder={formatMessage(
                messages["taskCreation.auth.register.password.placeholder"],
                { min: PASSWORD_MIN_LENGTH }
              )}
              {...register("password", {
                required: formatMessage(
                  messages["taskCreation.auth.password.required"]
                ),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: formatMessage(
                    messages["taskCreation.validation.minLength"],
                    { min: PASSWORD_MIN_LENGTH }
                  ),
                },
              })}
            />
          </AuthFormControl>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <PhoneInput
                id={FIELD_IDS.phone}
                label={formatMessage(
                  messages["taskCreation.auth.register.phone.label"]
                )}
                initialCountry={getCurrentCountry()}
                height={authFieldStyles.height}
                onChange={field.onChange}
                onBlur={field.onBlur}
                onValidityChange={setIsPhoneValid}
                isError={Boolean(errors.phoneNumber)}
                errorMessage={errors.phoneNumber?.message}
                disabled={isSubmitting}
                locale={locale}
              />
            )}
          />
          <Divider borderColor="gray.300" />
          <Checkbox {...authCheckboxStyles} {...register("proClient")}>
            <Text fontSize="sm" lineHeight="1.5" color="gray.700">
              <FormattedMessage
                {...messages["taskCreation.auth.register.business.explanation"]}
              />
            </Text>
          </Checkbox>
          <Divider borderColor="gray.300" />
          <Checkbox {...authCheckboxStyles} {...register("consentEmail")}>
            <Text fontSize="sm" lineHeight="1.5" color="gray.700">
              <FormattedMessage
                {...messages["taskCreation.auth.register.newsletter"]}
              />
            </Text>
          </Checkbox>
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            {...authSubmitStyles}
          >
            <FormattedMessage {...messages["taskCreation.cta.continue"]} />
          </Button>
        </VStack>
      </form>
    </AuthStepLayout>
  );
};
